import { graphHooks } from '@tunasong/graph-lib/react'
import { useSelector } from '@tunasong/redux'
import { usePageMetadata } from '@tunasong/tunasong-ui'
import { Button, NavLink, ProfileAvatarImage, Skeleton, Typography, useTunaMediaQuery } from '@tunasong/ui-lib'
import cn from 'classnames'
import { type FC, useEffect } from 'react'
import styles from './profile-entity-page.module.css'

export interface ProfileEntityPageProps {
  /** A profile's id is its user's id */
  userId: string
}

const statusOf = (error: unknown) =>
  error && typeof error === 'object' && 'status' in error ? (error as { status: unknown }).status : undefined

/** GH #2703: the entity page of a profile. Reads the profile, which any signed-in user may, never the entity. */
export const ProfileEntityPage: FC<ProfileEntityPageProps> = props => {
  const { userId } = props

  const currentUserId = useSelector(state => state.user.userId)
  const { profile, error, isError, refetch } = graphHooks.useProfile(userId)
  const { setName } = usePageMetadata()
  const isPhone = useTunaMediaQuery('(max-width: 599.95px)')

  const isOwner = Boolean(profile && profile.userId === currentUserId)
  /** Other users' profiles may carry a member's e-mail, which only the owner's own page shows */
  const email = isOwner ? profile?.email : undefined
  const title = profile ? (profile.name ?? email ?? 'Unnamed user') : undefined

  useEffect(() => {
    if (title) {
      setName(title)
    }
  }, [setName, title])

  if (!profile && isError) {
    return statusOf(error) === 404 ? (
      <div className={styles.root} role="status">
        <Typography variant="subtitle1">This profile doesn&apos;t exist.</Typography>
        <NavLink className={styles.link} to="/app">
          Go home
        </NavLink>
      </div>
    ) : (
      <div className={styles.root} role="status">
        <Typography variant="subtitle1">Couldn&apos;t load this profile.</Typography>
        <Button color="primary" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    )
  }

  const avatarPx = isPhone ? 72 : 96

  if (!profile) {
    return (
      <div className={styles.root} data-testid="profile-skeleton" aria-busy="true">
        <Skeleton variant="circular" width={avatarPx} height={avatarPx} />
        <Skeleton variant="text" width={160} />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <ProfileAvatarImage profile={profile} px={avatarPx} className={styles.avatar} alt="" />
      <Typography variant="h4" component="h1" className={styles.name}>
        {title}
      </Typography>
      {isOwner && profile.name && email ? <p className={styles.email}>{email}</p> : null}
      {isOwner && !profile.name ? (
        <NavLink className={styles.link} to="/app/profile">
          Add a name so collaborators recognize you.
        </NavLink>
      ) : null}
      {isOwner ? (
        <NavLink className={cn(styles.link, styles.edit)} to="/app/profile">
          Edit profile
        </NavLink>
      ) : null}
    </div>
  )
}

export default ProfileEntityPage
